import { onAuthStateChanged, type User } from "firebase/auth";
import { Component, lazy, Suspense, useEffect, useState, type ReactNode } from "react";
import { Navigate, Route, Routes } from "react-router-dom";

import { HomePage } from "./components/HomePage";
import { AccountAccessScreen, HostAccessScreen } from "./components/HostChrome";
import { auth } from "./lib/firebase";
import { clearPlayerProfileCache } from "./lib/playerProfileCache";
import { clearAccountRequests } from "./lib/accountRequestCache";
import { preloadRoute, routeModules } from "./lib/routePreload";

const AccountDashboard = lazy(() => routeModules.account().then((module) => ({ default: module.AccountDashboard })));
const HostHub = lazy(() => routeModules.host().then((module) => ({ default: module.HostHub })));
const HostBuilder = lazy(() => routeModules.trivia().then((module) => ({ default: module.HostBuilder })));
const RandomDraw = lazy(() => routeModules.draw().then((module) => ({ default: module.RandomDraw })));
const PartyBuilder = lazy(() => routeModules.party().then((module) => ({ default: module.PartyBuilder })));
const PublicDraw = lazy(() => routeModules.publicDraw().then((module) => ({ default: module.PublicDraw })));
const GameRoom = lazy(() => routeModules.room().then((module) => ({ default: module.GameRoom })));

class RouteErrorBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  state = { failed: false };
  static getDerivedStateFromError() { return { failed: true }; }
  render() {
    if (this.state.failed) return <main className="center-state"><div><h1>Let’s reconnect.</h1><p>This screen couldn’t load. Refresh to try again.</p><button className="primary-action" onClick={() => window.location.reload()}>Refresh page</button></div></main>;
    return this.props.children;
  }
}

function HostRoute({ user, children }: { user: User | null | undefined; children: ReactNode }) {
  if (!user || user.isAnonymous) return <HostAccessScreen user={user} loading={user === undefined} />;
  return children;
}

function App() {
  const [user, setUser] = useState<User | null | undefined>(() => auth.currentUser ?? undefined);

  useEffect(() => {
    let previousUid = auth.currentUser?.uid;
    return onAuthStateChanged(auth, (nextUser) => {
      const nextUid = nextUser && !nextUser.isAnonymous ? nextUser.uid : undefined;
      if (!nextUid || previousUid !== nextUid) clearAccountRequests();
      if (!nextUid) clearPlayerProfileCache();
      else if (previousUid && previousUid !== nextUid) clearPlayerProfileCache(previousUid);
      previousUid = nextUid;
      setUser(nextUser);
    });
  }, []);

  useEffect(() => {
    const preloadLink = (event: Event) => {
      const link = event.target instanceof Element ? event.target.closest("a[href]") : null;
      if (!(link instanceof HTMLAnchorElement) || link.origin !== window.location.origin) return;
      preloadRoute(link.pathname);
    };
    document.addEventListener("pointerover", preloadLink);
    document.addEventListener("focusin", preloadLink);
    document.addEventListener("touchstart", preloadLink, { passive: true });
    return () => {
      document.removeEventListener("pointerover", preloadLink);
      document.removeEventListener("focusin", preloadLink);
      document.removeEventListener("touchstart", preloadLink);
    };
  }, []);

  return (
    <RouteErrorBoundary><Suspense fallback={<div className="center-state" role="status">Opening your quest…</div>}>
      <Routes>
        <Route path="/" element={<HomePage user={user} />} />
        <Route path="/login" element={user && !user.isAnonymous ? <Navigate to="/account" replace /> : <AccountAccessScreen user={user} loading={user === undefined} />} />
        <Route path="/account/teams" element={<Navigate to="/account" replace />} />
        <Route path="/account/*" element={user && !user.isAnonymous ? <AccountDashboard key={user.uid} user={user} /> : <AccountAccessScreen user={user} loading={user === undefined} />} />
        <Route path="/host" element={<HostRoute user={user}><HostHub user={user} /></HostRoute>} />
        <Route path="/host/trivia" element={<HostRoute user={user}><HostBuilder key={user?.uid} user={user} /></HostRoute>} />
        <Route path="/host/random-draw" element={<HostRoute user={user}><RandomDraw key={user?.uid} user={user} /></HostRoute>} />
        <Route path="/host/two-truths" element={<HostRoute user={user}><PartyBuilder key={`truths-${user?.uid}`} gameType="truths" user={user} /></HostRoute>} />
        <Route path="/host/odd-one-out" element={<HostRoute user={user}><PartyBuilder key={`spy-${user?.uid}`} gameType="spy" user={user} /></HostRoute>} />
        <Route path="/host/sketch-whisper" element={<HostRoute user={user}><PartyBuilder key={`sketch-${user?.uid}`} gameType="sketch" user={user} /></HostRoute>} />
        <Route path="/host/night-before-gfc" element={<HostRoute user={user}><PartyBuilder key={`economy-${user?.uid}`} gameType="economy" user={user} /></HostRoute>} />
        <Route path="/host/rba-vs-economy" element={<Navigate to="/host/night-before-gfc" replace />} />
        <Route path="/host/spy" element={<Navigate to="/host/odd-one-out" replace />} />
        <Route path="/draw/:code" element={<PublicDraw />} />
        <Route path="/room/:code" element={<GameRoom user={user} />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense></RouteErrorBoundary>
  );
}

export default App;
